/**
 * API Configuration
 * Centralized backend URL management
 */

/**
 * Get the backend URL dynamically
 * @returns {string} The backend URL
 */
export const getBackendUrl = () => {
  // In production, always use REACT_APP_BACKEND_URL
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  
  // In development, use REACT_APP_BACKEND_URL or fallback to localhost
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
};

/**
 * Get the frontend/widget base URL for PREVIEWS (uses current origin)
 * @returns {string} The frontend base URL
 */
export const getFrontendUrl = () => {
  // For previews within the app, use current origin
  return window.location.origin;
};

/**
 * Get the PRODUCTION widget URL for embed codes
 * This should always be the Render-hosted production URL
 * @returns {string} The production widget base URL
 */
export const getWidgetProductionUrl = () => {
  // Use environment variable if set, otherwise use production domain
  return process.env.REACT_APP_WIDGET_URL || 'https://bookingflows.com';
};

/**
 * Get the PRODUCTION backend API URL
 * bookingflows.com is a static frontend - this returns the actual backend
 * Use REACT_APP_PRODUCTION_BACKEND_URL env var to configure
 * 
 * For embed codes/direct links:
 * - Dev/Preview environments: Use REACT_APP_BACKEND_URL (current environment's backend)
 * - Production: Use REACT_APP_PRODUCTION_BACKEND_URL (Render-hosted production backend)
 * 
 * @returns {string} The backend API URL for embed codes
 */
export const getProductionBackendUrl = () => {
  // If REACT_APP_PRODUCTION_BACKEND_URL is explicitly set, use it
  if (process.env.REACT_APP_PRODUCTION_BACKEND_URL) {
    return process.env.REACT_APP_PRODUCTION_BACKEND_URL;
  }
  
  // Otherwise, use the current environment's backend URL
  // This ensures dev/preview environments use their own backend
  return process.env.REACT_APP_BACKEND_URL || 'https://bookingflows-zviu.onrender.com';
};

/**
 * The backend base URL
 * @type {string}
 */
export const BACKEND_URL = getBackendUrl();

/**
 * The frontend base URL (for previews)
 * @type {string}
 */
export const FRONTEND_URL = getFrontendUrl();

/**
 * The production widget URL (for embed codes that operators copy)
 * @type {string}
 */
export const WIDGET_PRODUCTION_URL = getWidgetProductionUrl();

/**
 * The production backend URL (for calendar feeds, webhooks, etc.)
 * @type {string}
 */
export const PRODUCTION_BACKEND_URL = getProductionBackendUrl();

/**
 * The API base URL (backend + /api)
 * @type {string}
 */
export const API_URL = `${BACKEND_URL}/api`;

export default {
  BACKEND_URL,
  FRONTEND_URL,
  WIDGET_PRODUCTION_URL,
  PRODUCTION_BACKEND_URL,
  API_URL,
  getBackendUrl,
  getFrontendUrl,
  getWidgetProductionUrl,
  getProductionBackendUrl
};